import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import MonsterAvatar from '../components/MonsterAvatar'
import MonsterCard from '../components/MonsterCard'
import { FAMILIES } from '../game/families'
import { equippedTitle, passStatus } from '../game/pass'
import { rankFromPoints } from '../game/progression'
import { QUEST_REWARD_POINTS, questStatuses } from '../game/quests'
import { loadRecord } from '../game/storage'
import { useMonsters } from '../web3/useMonsters'

export default function Home() {
  const { monsters } = useMonsters()
  const navigate = useNavigate()
  const record = useMemo(() => loadRecord(), [])
  const rank = rankFromPoints(record.points)
  const quests = useMemo(() => questStatuses(), [])
  const pass = passStatus()
  const title = equippedTitle()

  // Première minute: pas de dashboard — tes monstres, un bouton, un combat.
  if (record.wins + record.losses === 0) {
    const starters = monsters.filter((m) => !m.hatching).slice(0, 2)
    return (
      <div className="flex flex-col items-center gap-6 pt-8 text-center">
        <div>
          <h1 className="text-2xl font-black uppercase tracking-tight">
            Tes monstres sont <span className="text-cyber">prêts</span>.
          </h1>
          <p className="mt-1 text-sm text-white/50">Combat de 60 secondes. Trois boutons. Zéro pitié.</p>
        </div>
        <div className="flex items-end justify-center gap-6">
          {starters.map((m) => (
            <div key={m.id} className="anim-pop text-center">
              <MonsterAvatar monster={m} size={120} />
              <p className="mt-1 text-sm font-extrabold">{m.name}</p>
              <p className="text-[10px] font-bold" style={{ color: FAMILIES[m.family].color }}>
                {FAMILIES[m.family].emoji} {FAMILIES[m.family].tagline}
              </p>
            </div>
          ))}
        </div>
        <button
          onClick={() => navigate('/battle', { state: { instant: true } })}
          className="w-full max-w-sm rounded-2xl bg-cyber py-5 text-xl font-black uppercase tracking-wide text-carbon transition-transform hover:scale-[1.02] active:scale-95"
          style={{ boxShadow: '0 6px 40px rgba(34,211,238,0.45)' }}
        >
          ⚡ Premier combat
        </button>
        <Link to="/battle" className="text-xs font-bold text-white/40 hover:text-cyber">
          ou choisis ton combattant →
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate('/battle', { state: { instant: true } })}
          className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-cyber py-4 text-lg font-black uppercase tracking-wide text-carbon transition-transform hover:scale-[1.01] active:scale-95"
          style={{ boxShadow: '0 4px 30px rgba(34,211,238,0.35)' }}
        >
          ⚡ Combat éclair
        </button>
        <Link
          to="/mint"
          className="rounded-2xl border border-line bg-panel px-5 py-4 text-lg font-black transition-transform hover:scale-[1.02] active:scale-95"
        >
          ✨
        </Link>
      </div>

      <div className="flex items-center gap-3 rounded-2xl border border-line bg-panel px-4 py-3">
        <span className="text-3xl">{rank.emoji}</span>
        <div className="flex-1">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-black uppercase tracking-wider">
              {rank.name}
              {title && (
                <span className="ml-1.5 text-[10px] font-bold normal-case text-eth">
                  {title.emoji} {title.label}
                </span>
              )}
            </span>
            <span className="text-xs font-bold text-white/50">
              {record.points} pts
              {rank.next && <span className="text-white/30"> / {rank.next.min}</span>}
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
              className="hp-bar-fill h-full rounded-full bg-cyber"
              style={{ width: `${rank.pct}%` }}
            />
          </div>
          <div className="mt-1 flex justify-between text-[10px] font-bold text-white/40">
            <span>
              {record.streak >= 2 ? `🔥 série de ${record.streak}` : `record de série : ${record.bestStreak}`}
            </span>
            {rank.next && (
              <span>
                prochain rang : {rank.next.emoji} {rank.next.name}
              </span>
            )}
          </div>
        </div>
      </div>

      <Link
        to="/pass"
        className="flex items-center gap-3 rounded-2xl border border-eth/30 bg-panel px-4 py-2.5 transition-colors hover:border-eth/60"
      >
        <span className="text-xl">🎫</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between text-[11px] font-bold">
            <span className="font-black uppercase tracking-wider text-white/70">
              Pass S1 · niv. {pass.level}
            </span>
            <span className="text-white/40">
              {pass.nextFree ? `prochain : ${pass.nextFree.emoji} ${pass.nextFree.label}` : 'complet 👑'}
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div className="hp-bar-fill h-full rounded-full bg-eth" style={{ width: `${pass.pct}%` }} />
          </div>
        </div>
      </Link>

      <div className="rounded-2xl border border-line bg-panel px-4 py-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xs font-black uppercase tracking-wider text-white/60">
            🎯 Quêtes du jour
          </h2>
          <span className="text-[10px] font-bold text-white/35">
            +{QUEST_REWARD_POINTS} pts chacune · les mêmes pour tous
          </span>
        </div>
        <div className="mt-2 space-y-1.5">
          {quests.map(({ quest, progress, done }) => (
            <div key={quest.id} className="flex items-center gap-2">
              <span className={`text-sm ${done ? '' : 'opacity-30'}`}>{done ? '✅' : '⬜'}</span>
              <span className={`flex-1 text-xs font-bold ${done ? 'text-white/35 line-through' : 'text-white/75'}`}>
                {quest.text}
              </span>
              <span className={`text-[10px] font-black tabular-nums ${done ? 'text-stable' : 'text-white/40'}`}>
                {done ? 'FAIT' : `${progress}/${quest.target}`}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 text-center">
        <div className="flex-1 rounded-xl border border-line bg-panel py-2">
          <p className="text-xl font-black text-stable">{record.wins}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">Victoires</p>
        </div>
        <div className="flex-1 rounded-xl border border-line bg-panel py-2">
          <p className="text-xl font-black text-meme">{record.losses}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">Défaites</p>
        </div>
        <div className="flex-1 rounded-xl border border-line bg-panel py-2">
          <p className="text-xl font-black text-cyber">{monsters.length}</p>
          <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">Monstres</p>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-sm font-black uppercase tracking-wider text-white/60">Ton équipe</h2>
          <Link to="/collection" className="text-xs font-bold text-cyber">
            Tout voir →
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {monsters.slice(0, 6).map((m) => (
            <MonsterCard key={m.id} monster={m} compact />
          ))}
        </div>
      </div>
    </div>
  )
}
